import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import i18n from '../i18n/config';
import { logger } from '../utils/logger';
import { isPublicAppPath, isPublicDemoAccessPath } from '../utils/publicAccessPaths';

// 開発環境ではViteのプロキシを使用、本番環境では環境変数から取得
const getBaseURL = () => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  // 開発環境ではプロキシを使用（/api -> http://localhost:8000）
  if (import.meta.env.DEV) {
    return '/api';
  }
  // 本番環境のデフォルト（Nginx経由で配信される場合は /api を使用）
  return '/api';
};

const api = axios.create({
  baseURL: getBaseURL(),
  withCredentials: true,
  timeout: 60000, // 60秒
  headers: {
    'Content-Type': 'application/json',
  },
});

let reconfirmInflight: Promise<boolean> | null = null;

function askPasswordAgain(): Promise<boolean> {
  if (reconfirmInflight) return reconfirmInflight;
  reconfirmInflight = new Promise((resolve) => {
    const wrap = document.createElement('div');
    wrap.style.cssText =
      'position:fixed;inset:0;background:rgba(15,23,42,.48);z-index:2000;display:flex;align-items:center;justify-content:center;padding:16px;';
    const card = document.createElement('form');
    card.style.cssText =
      'background:#fff;border-radius:16px;padding:24px;width:min(420px,100%);box-shadow:0 24px 48px rgba(15,23,42,.22);';
    card.innerHTML =
      '<p style="margin:0 0 6px;font-size:1.15rem;font-weight:700;color:#0f172a">パスワードの確認</p>' +
      '<p style="margin:0 0 16px;color:#475569;font-size:14px;line-height:1.6">利用者の変更やバックアップの前に、本人確認のためもう一度入力します。</p>' +
      '<label for="reconfirm-password" style="display:block;margin-bottom:6px;font-size:14px;font-weight:600;color:#334155">パスワード</label>' +
      '<input id="reconfirm-password" type="password" name="password" autocomplete="current-password" style="width:100%;padding:10px 12px;border:1px solid #cbd5e1;border-radius:10px;font-size:16px" />' +
      '<p data-error hidden style="margin:8px 0 0;color:#b91c1c;font-size:13px"></p>' +
      '<div style="display:flex;gap:8px;justify-content:flex-end;margin-top:18px">' +
      '<button type="button" data-cancel style="padding:9px 14px;border:0;background:#e2e8f0;color:#0f172a;border-radius:10px;font-weight:600">キャンセル</button>' +
      '<button type="submit" data-ok style="padding:9px 16px;border:0;background:#2563eb;color:#fff;border-radius:10px;font-weight:600">確認して続ける</button>' +
      '</div>';
    wrap.appendChild(card);
    const input = card.querySelector('input') as HTMLInputElement;
    const error = card.querySelector('[data-error]') as HTMLParagraphElement;
    const okButton = card.querySelector('[data-ok]') as HTMLButtonElement;
    const close = (value: boolean) => {
      wrap.remove();
      resolve(value);
    };
    card.querySelector('[data-cancel]')?.addEventListener('click', () => close(false));
    wrap.addEventListener('click', (event) => {
      if (event.target === wrap) close(false);
    });
    card.addEventListener('submit', (event) => {
      event.preventDefault();
      if (!input.value) {
        error.hidden = false;
        error.textContent = 'パスワードを入力してください。';
        input.focus();
        return;
      }
      error.hidden = true;
      okButton.disabled = true;
      okButton.textContent = '確認しています…';
      void csrfFetchClient
        .post(
          '/auth/reconfirm',
          { password: input.value },
          {
            headers: localStorage.getItem('token')
              ? { Authorization: `Bearer ${localStorage.getItem('token')}` }
              : undefined,
          }
        )
        .then(() => close(true))
        .catch(() => {
          okButton.disabled = false;
          okButton.textContent = '確認して続ける';
          error.hidden = false;
          error.textContent = 'パスワードが違います。もう一度入力してください。';
          input.select();
        });
    });
    document.body.appendChild(wrap);
    input.focus();
  }).finally(() => {
    reconfirmInflight = null;
  });
  return reconfirmInflight;
}

/** CSRF 取得専用: メイン api のリクエストインターセプターを通さない（再入・待ち行列のリスクを排除） */
const csrfFetchClient = axios.create({
  baseURL: getBaseURL(),
  withCredentials: true,
  timeout: 20000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// CSRFトークンのキャッシュ（セッション中は再利用）
let csrfTokenCache: string | null = null;

const getCsrfToken = async (): Promise<string | null> => {
  if (csrfTokenCache) {
    return csrfTokenCache;
  }
  try {
    const bearer = localStorage.getItem('token');
    const response = await csrfFetchClient.get<{ success?: boolean; data?: { csrfToken?: string } }>(
      '/auth/csrf-token',
      bearer ? { headers: { Authorization: `Bearer ${bearer}` } } : undefined
    );
    if (response.data?.success && response.data?.data?.csrfToken) {
      csrfTokenCache = response.data.data.csrfToken;
      return csrfTokenCache;
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      logger.warn('Failed to get CSRF token', error);
    }
  }
  return null;
};

/** バックエンドで csrfProtection を掛けていないルートは先行 CSRF GET を省略する */
function shouldSkipCsrfAttachment(url: string | undefined): boolean {
  if (!url) return false;
  return (
    url.includes('/guest') ||
    url.includes('/password-reset') ||
    url.includes('/authenticate') ||
    url.includes('/access-logs/public-page-view') ||
    url.includes('/access-logs/feature-entrance') ||
    url.includes('feature-requests')
  );
}

// リクエストインターセプター
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // 認証トークンを追加
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // UI 言語を優先（ブラウザ Accept-Language=en でも ja UI なら氏名語順を揃える）
    const appLang = (i18n.language || 'ja').split('-')[0];
    if (appLang) {
      config.headers['Accept-Language'] = i18n.language || appLang;
    }

    // 書き込み操作（POST, PUT, DELETE, PATCH）にCSRFトークンを追加
    const method = config.method?.toUpperCase();
    if (method && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
      if (!shouldSkipCsrfAttachment(config.url)) {
        const csrfToken = await getCsrfToken();
        if (csrfToken) {
          // ヘッダーにCSRFトークンを追加
          config.headers['X-CSRF-Token'] = csrfToken;
          // ボディにも追加（フォーム送信の場合に備えて）
          if (config.data && typeof config.data === 'object' && !(config.data instanceof FormData)) {
            config.data._csrf = csrfToken;
          }
        }
      }
    }

    // リクエストログ（開発環境のみ）
    if (import.meta.env.DEV) {
      const url = String(config.url || '');
      logger.debug(`API Request: ${config.method?.toUpperCase()} ${url}`, {
        data: url.includes('/auth/') ? undefined : config.data,
        params: config.params,
      });
    }

    return config;
  },
  (error) => {
    logger.error('Request interceptor error', error);
    return Promise.reject(error);
  }
);

// レスポンスインターセプター
api.interceptors.response.use(
  (response) => {
    // レスポンスログ（開発環境のみ）
    if (import.meta.env.DEV) {
      logger.debug(`API Response: ${response.config.method?.toUpperCase()} ${response.config.url}`, {
        status: response.status,
      });
    }
    return response;
  },
  async (error: AxiosError) => {
    const reconfirmMessage = (error.response?.data as { error?: { message?: string } } | undefined)?.error
      ?.message;
    const reconfirmConfig = error.config as InternalAxiosRequestConfig & { __reconfirmRetried?: boolean };
    if (
      error.response?.status === 403 &&
      reconfirmMessage === 'PASSWORD_RECONFIRM_REQUIRED' &&
      reconfirmConfig &&
      !reconfirmConfig.__reconfirmRetried &&
      !String(reconfirmConfig.url || '').includes('/auth/reconfirm')
    ) {
      const confirmed = await askPasswordAgain();
      if (confirmed) {
        reconfirmConfig.__reconfirmRetried = true;
        return api.request(reconfirmConfig);
      }
    }
    // CSRFトークンエラー（403）の場合、トークンを再取得して1回だけリトライ
    if (error.response?.status === 403 && error.config && !(error.config as InternalAxiosRequestConfig & { __csrfRetried?: boolean }).__csrfRetried) {
      const errorData = error.response?.data as any;
      const errorMessage = errorData?.error?.message ?? '';
      const errorCode = errorData?.error?.code;
      const isCsrfError =
        errorCode === 'csrf.token_missing' ||
        errorCode === 'csrf.token_invalid' ||
        (typeof errorMessage === 'string' && (errorMessage.includes('CSRF') || errorMessage.includes('csrf')));
      if (isCsrfError) {
        csrfTokenCache = null;
        const csrfToken = await getCsrfToken();
        if (csrfToken) {
          (error.config as InternalAxiosRequestConfig & { __csrfRetried?: boolean }).__csrfRetried = true;
          if (error.config.headers) {
            error.config.headers['X-CSRF-Token'] = csrfToken;
          }
          // ボディが既に文字列化されている場合はパースして _csrf を付与してから再送
          if (error.config.data) {
            if (typeof error.config.data === 'string') {
              try {
                const parsed = JSON.parse(error.config.data) as Record<string, unknown>;
                parsed._csrf = csrfToken;
                error.config.data = JSON.stringify(parsed);
              } catch {
                // パースできない場合はヘッダーのみでリトライ
              }
            } else if (typeof error.config.data === 'object' && !(error.config.data instanceof FormData)) {
              (error.config.data as Record<string, unknown>)._csrf = csrfToken;
            }
          }
          return api.request(error.config);
        }
      }
    }
    // 接続エラーの場合（サーバーが起動していない等）
    if (!error.response) {
      if (error.code === 'ERR_NETWORK' || error.message?.includes('ERR_CONNECTION_REFUSED')) {
        logger.error('Network error: Backend server is not available', error, {
          url: error.config?.url,
          method: error.config?.method,
        });
        const customError = new Error('api.error.network_connection_failed');
        (customError as any).isNetworkError = true;
        (customError as any).translationKey = 'api.error.network_connection_failed';
        return Promise.reject(customError);
      }

      if (error.code === 'ECONNABORTED') {
        logger.error('Request timeout', error, {
          url: error.config?.url,
          method: error.config?.method,
        });
        const customError = new Error('api.error.request_timeout');
        (customError as any).translationKey = 'api.error.request_timeout';
        return Promise.reject(customError);
      }
    }

    // HTTPエラーレスポンス
    if (error.response) {
      const status = error.response.status;
      const errorData = error.response.data as any;

      // 404/409 は UI で扱う想定。409（重複など）はコンソールを汚さない
      if (status === 404) {
        logger.debug(`API Error: ${status} ${error.config?.method?.toUpperCase()} ${error.config?.url} - Resource not found`, {
          status,
          url: error.config?.url,
        });
      } else if (status === 409) {
        logger.debug(`API ${status} ${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
          status,
          errorData,
        });
      } else if (status === 400) {
        logger.warn(`API ${status} ${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
          status,
          errorData,
        });
      } else {
        logger.error(`API Error: ${status} ${error.config?.method?.toUpperCase()} ${error.config?.url}`, error, {
          status,
          errorData,
        });
      }

      if (status === 401) {
        const currentPath = window.location.pathname;
        const isGaisukeArenaPublicApi =
          error.config?.url?.includes('/gaisuke/arena/') ||
          error.config?.url?.includes('/gaisuke/public/');
        const isKeibaArenaPublicApi = error.config?.url?.includes('/keiba-arena/public/');
        const isBanquetPublicApi = error.config?.url?.includes('/banquet/public/');
        const isEconReportPublicApi = error.config?.url?.includes('/econ-report/public/');
        const isReservationPublicApi = error.config?.url?.includes('/reservation-system/public/');
        const isAuthMeEndpoint = error.config?.url?.includes('/auth/me');

        if (
          isPublicDemoAccessPath(currentPath) ||
          isReservationPublicApi ||
          isGaisukeArenaPublicApi ||
          isKeibaArenaPublicApi ||
          isBanquetPublicApi ||
          isEconReportPublicApi
        ) {
          // 公開デモ・共有 API は認証不要
        } else if (isAuthMeEndpoint) {
          logger.debug('Authentication error on /auth/me: Will be handled by useAuth', {
            url: error.config?.url,
          });
        } else if (!isPublicAppPath(currentPath)) {
          logger.warn('Authentication error: Token expired or invalid. Redirecting to login', {
            url: error.config?.url,
            currentPath,
          });
          localStorage.removeItem('token');
          setTimeout(() => {
            window.location.href = '/login';
          }, 100);
        } else {
          logger.debug('Authentication error on public route: Not redirecting', {
            url: error.config?.url,
            currentPath,
          });
          localStorage.removeItem('token');
        }
      }

      if (status === 429) {
        // レート制限エラー（メッセージはサーバーから、表示はi18nで統一するためtranslationKeyを付与）
        const customError = new Error(
          errorData?.error?.message || 'api.error.too_many_requests'
        ) as Error & { httpStatus?: number; translationKey?: string };
        customError.httpStatus = status;
        customError.translationKey =
          errorData?.error?.code || 'api.error.too_many_requests';
        return Promise.reject(customError);
      }

      // 408 Request Timeout / 499 Client Closed Request（nginx 等）
      // Axios の生文言 "Request failed with status code 499" を画面に出さない
      if (status === 408 || status === 499) {
        const customError = new Error('api.error.request_timeout') as Error & {
          httpStatus?: number;
          translationKey?: string;
        };
        customError.httpStatus = status;
        customError.translationKey = 'api.error.request_timeout';
        return Promise.reject(customError);
      }

      if (status >= 500) {
        // サーバーエラー（504 タイムアウト含む）
        const customError = new Error(
          errorData?.error?.message || 'api.error.server_error'
        ) as Error & { httpStatus?: number; translationKey?: string };
        customError.httpStatus = status;
        if (!errorData?.error?.message) {
          customError.translationKey = 'api.error.server_error';
        }
        return Promise.reject(customError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
export { api };

